import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { QueryKey } from '../types/growth-query-key';

interface MigrationResult {
	migrated_count: number;
}

interface ClearResult {
	cleared_count: number;
}

/**
 * Hook for migrating V1 1:1 meetings to V2.
 * - migrate(): migrates all eligible V1 meetings to V2
 * - clear(): deletes all migrated V2 meetings (allows re-migration)
 */
export const useMigration = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	const migrate = useMutation({
		mutationFn: async (): Promise<MigrationResult> => {
			const response = await http.post('/one_on_ones/migration/migrate');
			return response.data;
		},
		onSuccess: () => {
			// invalidate meetings list to show migrated meetings
			queryClient.invalidateQueries([QueryKey.MeetingsV2]);
		},
	});

	const clear = useMutation({
		mutationFn: async (): Promise<ClearResult> => {
			const response = await http.delete('/one_on_ones/migration/clear');
			return response.data;
		},
		onSuccess: () => {
			// invalidate meetings list to remove cleared meetings
			queryClient.invalidateQueries([QueryKey.MeetingsV2]);
		},
	});

	return {
		migrate,
		clear,
		isMigrating: migrate.isLoading,
		isClearing: clear.isLoading,
		isLoading: migrate.isLoading || clear.isLoading,
	};
};
